import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Trophy, Sparkles, Moon, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const messages = [
  { title: "That's a beautiful rest", body: "Your body just banked real recovery. Deep sleep restores memory, immunity, and mood — you're stacking wins." },
  { title: "Circadian gold ⭐", body: "Quality sleep before a shift is the single strongest predictor of on-the-job performance. You showed up for tomorrow-you." },
  { title: "Well done, shift warrior", body: "Long shifts push most people into debt — you just paid yours down. Keep this rhythm and your score climbs fast." },
  { title: "Restored & recharged", body: "Great sleep = sharper decisions, faster reflexes, calmer mood. Your patients / crew / passengers benefit too." },
  { title: "Sleep MVP", body: "You beat the shift-worker average tonight. Small win today, huge compounding effect over 30 days." },
];

export default function RewardModal({ open, onOpenChange, sleepEntry }) {
  const msg = useMemo(() => messages[Math.floor(Math.random() * messages.length)], [open]);
  const [stars, setStars] = useState(0);

  useEffect(() => {
    if (!open) { setStars(0); return; }
    const q = sleepEntry?.quality || 4;
    let i = 0;
    const t = setInterval(() => {
      i++;
      setStars(i);
      if (i >= q) clearInterval(t);
    }, 140);
    return () => clearInterval(t);
  }, [open, sleepEntry]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-md rounded-2xl overflow-hidden">
        <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 w-52 h-52 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

        <DialogHeader className="relative">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center glow-amber mb-2">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-center font-display text-2xl font-extrabold tracking-tight">
            {msg.title}
          </DialogTitle>
        </DialogHeader>

        <div className="relative text-center">
          <div className="flex justify-center gap-1 py-2" data-testid="reward-stars">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`w-6 h-6 transition-all duration-300 ${
                  i < stars ? "text-amber-300 fill-amber-300 scale-110" : "text-slate-700"
                }`}
              />
            ))}
          </div>
          <p className="text-slate-300 text-sm leading-relaxed max-w-sm mx-auto mt-2">
            {msg.body}
          </p>

          {sleepEntry && (
            <div className="mt-5 grid grid-cols-3 gap-3">
              <StatMini label="Duration" value={`${sleepEntry.duration_hours}h`} />
              <StatMini label="Quality" value={`${sleepEntry.quality}/5`} />
              <StatMini label="Streak" value="+1" tint="text-emerald-300" />
            </div>
          )}

          <Button
            onClick={() => onOpenChange(false)}
            data-testid="reward-close-btn"
            className="mt-6 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
          >
            <Sparkles className="w-4 h-4 mr-1" /> Keep the streak going
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatMini({ label, value, tint = "text-indigo-300" }) {
  return (
    <div className="bg-slate-900/60 border border-white/10 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">{label}</div>
      <div className={`font-display text-lg font-bold ${tint}`}>{value}</div>
    </div>
  );
}
